// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
export class IndexedDB {
    constructor(config) {
        this.db = null;
        this.config = config;
    }
    openDB() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.db)
                return this.db;
            return yield new Promise((resolve, reject) => {
                const request = indexedDB.open(this.config.dbName, this.config.version);
                request.onupgradeneeded = () => {
                    const db = request.result;
                    this.config.stores.forEach((storeConfig) => {
                        var _a;
                        if (!db.objectStoreNames.contains(storeConfig.storeName)) {
                            const storeOptions = {
                                keyPath: storeConfig.keyPath,
                                autoIncrement: storeConfig.autoIncrement
                            };
                            const objectStore = db.createObjectStore(storeConfig.storeName, storeOptions);
                            (_a = storeConfig.indexes) === null || _a === void 0 ? void 0 : _a.forEach((index) => objectStore.createIndex(index.name, index.keyPath, index.options));
                        }
                    });
                };
                request.onsuccess = () => {
                    this.db = request.result;
                    resolve(this.db);
                };
                request.onerror = () => {
                    var _a;
                    reject(new Error(`Failed to open database: ${(_a = request.error) === null || _a === void 0 ? void 0 : _a.message}`));
                };
            });
        });
    }
    getObjectStore(storeName, transactionMode) {
        return __awaiter(this, void 0, void 0, function* () {
            const db = yield this.openDB();
            const transaction = db.transaction(storeName, transactionMode);
            return transaction.objectStore(storeName);
        });
    }
    addOrUpdate(storeName, item) {
        return __awaiter(this, void 0, void 0, function* () {
            const store = yield this.getObjectStore(storeName, 'readwrite');
            yield new Promise((resolve, reject) => {
                const request = store.put(item);
                request.onsuccess = () => {
                    resolve();
                };
                request.onerror = () => {
                    var _a;
                    reject(new Error(`Update failed: ${(_a = request.error) === null || _a === void 0 ? void 0 : _a.message}`));
                };
            });
            console.log(`Data updated successfully in ${storeName}`);
        });
    }
    get(storeName, key) {
        return __awaiter(this, void 0, void 0, function* () {
            const store = yield this.getObjectStore(storeName, 'readonly');
            return yield new Promise((resolve, reject) => {
                const request = store.get(key);
                request.onsuccess = () => {
                    resolve(request.result);
                };
                request.onerror = () => {
                    var _a;
                    reject(new Error(`Get failed: ${(_a = request.error) === null || _a === void 0 ? void 0 : _a.message}`));
                };
            });
        });
    }
    getAll(storeName) {
        return __awaiter(this, void 0, void 0, function* () {
            const store = yield this.getObjectStore(storeName, 'readonly');
            return yield new Promise((resolve, reject) => {
                const request = store.getAll();
                request.onsuccess = () => {
                    resolve(request.result);
                };
                request.onerror = () => {
                    var _a;
                    reject(new Error(`GetAll failed: ${(_a = request.error) === null || _a === void 0 ? void 0 : _a.message}`));
                };
            });
        });
    }
    delete(storeName, key) {
        return __awaiter(this, void 0, void 0, function* () {
            const store = yield this.getObjectStore(storeName, 'readwrite');
            yield new Promise((resolve, reject) => {
                const request = store.delete(key);
                request.onsuccess = () => {
                    resolve();
                };
                request.onerror = () => {
                    var _a;
                    reject(new Error(`Delete failed: ${(_a = request.error) === null || _a === void 0 ? void 0 : _a.message}`));
                };
            });
        });
    }
    clear(storeName) {
        return __awaiter(this, void 0, void 0, function* () {
            const store = yield this.getObjectStore(storeName, 'readwrite');
            yield new Promise((resolve, reject) => {
                const request = store.clear();
                request.onsuccess = () => {
                    resolve();
                };
                request.onerror = () => {
                    var _a;
                    reject(new Error(`Clear failed: ${(_a = request.error) === null || _a === void 0 ? void 0 : _a.message}`));
                };
            });
        });
    }
    // Reading by Index
    getByIndex(storeName, indexName, indexValue) {
        return __awaiter(this, void 0, void 0, function* () {
            const store = yield this.getObjectStore(storeName, 'readonly');
            const index = store.index(indexName);
            return yield new Promise((resolve, reject) => {
                const request = index.get(indexValue);
                request.onsuccess = () => {
                    resolve(request.result);
                };
                request.onerror = () => {
                    var _a;
                    reject(new Error(`Get by index failed: ${(_a = request.error) === null || _a === void 0 ? void 0 : _a.message}`));
                };
            });
        });
    }
    getAllByIndex(storeName, indexName, indexValue) {
        return __awaiter(this, void 0, void 0, function* () {
            const store = yield this.getObjectStore(storeName, 'readonly');
            const index = store.index(indexName);
            return yield new Promise((resolve, reject) => {
                const request = index.getAll(indexValue);
                request.onsuccess = () => {
                    resolve(request.result);
                };
                request.onerror = () => {
                    var _a;
                    reject(new Error(`GetAll by index failed: ${(_a = request.error) === null || _a === void 0 ? void 0 : _a.message}`));
                };
            });
        });
    }
    getAllInRangeByIndex(storeName, indexName, lowerBound, upperBound) {
        return __awaiter(this, void 0, void 0, function* () {
            const store = yield this.getObjectStore(storeName, 'readonly');
            const index = store.index(indexName);
            const range = IDBKeyRange.bound(lowerBound, upperBound);
            return yield new Promise((resolve, reject) => {
                const request = index.getAll(range);
                request.onsuccess = () => {
                    resolve(request.result);
                };
                request.onerror = () => {
                    var _a;
                    reject(new Error(`GetAll in range by index failed: ${(_a = request.error) === null || _a === void 0 ? void 0 : _a.message}`));
                };
            });
        });
    }
    deleteByIndex(storeName, indexName, indexValue) {
        return __awaiter(this, void 0, void 0, function* () {
            const store = yield this.getObjectStore(storeName, 'readwrite');
            const index = store.index(indexName);
            yield new Promise((resolve, reject) => {
                const request = index.openCursor(IDBKeyRange.only(indexValue));
                request.onsuccess = () => {
                    const cursor = request.result;
                    if (cursor) {
                        cursor.delete();
                        cursor.continue();
                    }
                    else {
                        resolve();
                    }
                };
                request.onerror = () => {
                    var _a;
                    reject(new Error(`Delete by index failed: ${(_a = request.error) === null || _a === void 0 ? void 0 : _a.message}`));
                };
            });
        });
    }
    deleteInRangeByIndex(storeName, indexName, lowerBound, upperBound) {
        return __awaiter(this, void 0, void 0, function* () {
            const store = yield this.getObjectStore(storeName, 'readwrite');
            const index = store.index(indexName);
            const range = IDBKeyRange.bound(lowerBound, upperBound);
            yield new Promise((resolve, reject) => {
                const request = index.openCursor(range);
                request.onsuccess = () => {
                    const cursor = request.result;
                    if (cursor) {
                        cursor.delete();
                        cursor.continue();
                    }
                    else {
                        resolve();
                    }
                };
                request.onerror = () => {
                    var _a;
                    reject(new Error(`Delete in range by index failed: ${(_a = request.error) === null || _a === void 0 ? void 0 : _a.message}`));
                };
            });
        });
    }
    closeDB() {
        if (this.db) {
            this.db.close();
            this.db = null;
        }
    }
}
